import { useEffect } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import { applySeo } from "@/utils/seo";

const principles = [
  {
    icon: "ri-check-double-line",
    title: "先核实，后报道",
    desc: "事实核查永远先于标题与流量。每一条独家在成稿前，都要先通过信源交叉与多方验证。",
  },
  {
    icon: "ri-lock-line",
    title: "守住边界与隐私",
    desc: "报道讲真相，也讲分寸。我们尊重当事人的合法权利，对无实证的猜测与越界的窥探保持克制。",
  },
  {
    icon: "ri-scales-3-line",
    title: "观点可以鲜明，事实必须中立",
    desc: "我们对现象可以有自己的判断，但对事实的呈现保持中立，不预设立场、不选择性裁剪。",
  },
  {
    icon: "ri-refresh-line",
    title: "接受质疑，持续更新",
    desc: "内容并非一成不变。接到可靠的新信息或异议时，我们会对已发报道进行更正、补充与追踪。",
  },
];

const steps = [
  { title: "线索接洽", desc: "多渠道接收信源线索，评估其价值与可核验空间。" },
  { title: "交叉核验", desc: "对关键信息进行多方交叉比对，剔除无法证实的部分。" },
  { title: "审稿把关", desc: "内部审稿对措辞、定性边界与信息来源逐条把关。" },
  { title: "追踪更新", desc: "发布后持续跟进新证据，及时更正与补充后续进展。" },
];

const voices = [
  {
    who: "一位互联网行业从业者",
    text: "做内容多年，难得看到把热搜拆得这么清楚又克制的文章。没有情绪化的指责，只有一环扣一环的推导。",
  },
  {
    who: "一位财经媒体读者",
    text: "关于加盟与融资的那几篇，把我看得脊背发凉。干货密度高，而且看得出一句是一句都有出处。",
  },
  {
    who: "一位栏目长期订阅用户",
    text: "订阅半年多，几乎没有收到过垃圾推送，每周那两篇重头戏都值得花时间读完。这样的内容不多了。",
  },
];

export default function About() {
  useEffect(() => {
    applySeo({
      title: "关于我们 — 灼见社 | 独家内幕与深度揭秘",
      description:
        "了解灼见社：一个专注独家内幕与深度揭秘的资讯平台。我们坚持先核实后报道，以知情人士视角还原幕后真相，恪守边界，接受质疑。",
      keywords: "灼见社,独家内幕,深度揭秘,关于我们,采编原则",
      path: "/about",
      type: "about",
      jsonLd: [
        {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://yusr.com.cn/" },
            { "@type": "ListItem", position: 2, name: "关于我们" },
          ],
        },
        {
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于灼见社",
          about:
            "一个专注独家内幕、深度揭秘与幕后真相报道的资讯平台。",
        },
      ],
    });
  }, []);

  return (
    <>
      <SiteHeader />
      <main>
        <section className="border-b border-background-200/70 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
            <nav
              aria-label="面包屑"
              className="mb-5 flex items-center gap-1.5 text-sm text-foreground-500"
            >
              <Link to="/" className="hover:text-primary-600">
                首页
              </Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700">关于我们</span>
            </nav>
            <div className="max-w-3xl">
              <span className="inline-flex items-center rounded bg-primary-500 px-2 py-0.5 text-xs font-medium text-background-50">
                ABOUT US
              </span>
              <h1 className="mt-4 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                在热搜与通稿之外，看见事件背后的真相
              </h1>
              <p className="mt-4 text-base leading-8 text-foreground-600">
                灼见社是一个专注"独家内幕、深度揭秘、幕后真相"的中文资讯平台。我们相信，真正重要的信息，往往不会出现在热搜榜单的最顶端，而藏在那些未被放大的细节与被略过的上下文里。为此，我们以知情人士视角切入，用可靠信源还原事件的完整拼图。
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="md:col-span-2">
              <h2 className="font-heading text-2xl font-bold text-foreground-950">
                我们报道什么
              </h2>
              <div className="mt-4 space-y-4 text-sm leading-7 text-foreground-700">
                <p>
                  我们把目光投向娱乐圈内幕、互联网背后、商业资本、科技幕后、民生观察与都市异闻等领域的结构性现象：从一场晚会座次的排位逻辑，到一条热搜被提前排好的剧本；从加盟招商的回本模型，到旧衣回收链条里被稀释的善意。
                </p>
                <p>
                  这些选题的共同点，是它们都在以某种"约定俗成"的方式运转，却极少被系统地讲清楚。我们的工作，就是把这些默不作声的规则摊开，让读者在理解之后，拥有更从容的判断。
                </p>
                <p>
                  我们关注现象而非个人隐私，追问机制而非发泄情绪。当揭示某个行业的运作方式能帮助读者更好地理解世界、做出更理性的选择时，便是灼见社最想做的事。
                </p>
              </div>
            </div>
            <div className="flex flex-col items-center justify-center gap-4 rounded-lg border border-background-200/70 bg-background-100 p-6">
              <div className="text-center">
                <p className="font-heading text-4xl font-black text-primary-600">300+</p>
                <p className="mt-1 text-xs text-foreground-600">累计深度原创</p>
              </div>
              <div className="text-center">
                <p className="font-heading text-4xl font-black text-accent-600">6</p>
                <p className="mt-1 text-xs text-foreground-600">垂直栏目</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">
            内容原则
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
            {principles.map((p) => (
              <div
                key={p.title}
                className="flex gap-4 rounded-lg border border-background-200/70 bg-background-100 p-5"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-primary-500 text-xl text-background-50">
                  <i className={p.icon}></i>
                </span>
                <div>
                  <h3 className="font-heading text-base font-bold text-foreground-950">
                    {p.title}
                  </h3>
                  <p className="mt-1.5 text-sm leading-6 text-foreground-600">{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">
            一篇深文的诞生
          </h2>
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <div key={s.title} className="relative rounded-lg border border-background-200/70 bg-background-50 p-5">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent-100 font-heading text-base font-bold text-accent-800">
                  {i + 1}
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {s.title}
                </h3>
                <p className="mt-1.5 text-sm leading-6 text-foreground-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 bg-background-100">
          <div className="mx-auto max-w-7xl px-4 py-12 md:px-6">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              读者怎么说
            </h2>
            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
              {voices.map((v) => (
                <figure
                  key={v.who}
                  className="flex flex-col rounded-lg border border-background-200/70 bg-background-50 p-5"
                >
                  <span className="flex items-center gap-1 text-accent-500">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-sm"></i>
                    ))}
                  </span>
                  <blockquote className="mt-3 flex-1 text-sm leading-7 text-foreground-700">
                    “{v.text}”
                  </blockquote>
                  <figcaption className="mt-4 text-xs text-foreground-500">
                    —— {v.who}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-4 pt-14 md:px-6">
          <div className="rounded-lg border border-background-200/70 bg-background-100 p-6 md:p-8">
            <h2 className="font-heading text-2xl font-bold text-foreground-950">
              联系我们
            </h2>
            <div className="mt-5 grid grid-cols-1 gap-6 md:grid-cols-3">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-500 text-background-50">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">线索与合作</p>
                  <a
                    href="mailto:contact@zhuojianshe.example"
                    className="mt-1 inline-block text-sm text-primary-600 hover:text-primary-700"
                  >
                    contact@zhuojianshe.example
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-accent-500 text-background-50">
                  <i className="ri-feedback-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">纠错与异议</p>
                  <p className="mt-1 text-sm text-foreground-600">
                    如发现内容有误，欢迎来信指正，核实后我们将及时更正。
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-500 text-background-50">
                  <i className="ri-news-line text-lg"></i>
                </span>
                <div>
                  <p className="text-sm font-medium text-foreground-900">订阅深度内容</p>
                  <Link
                    to="/#subscribe"
                    className="mt-1 inline-block text-sm text-primary-600 hover:text-primary-700"
                  >
                    前往订阅页 →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}